import { Typography, Tooltip } from '@mui/material';
import classNames from 'classnames';
import React from 'react';

import { Iconify } from '../../Iconify';
import './ListItem.scss';

export type ListItemProps = {
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => Promise<unknown>;
  /**
   * Disables selected highlight.
   */
  withoutSelectedHighlight?: boolean;
  tooltipLabelDescription?: string;
  style?: React.CSSProperties;
  disabledTooltip?: string;
  /**
   * Disables hover effect
   */
  withoutHover?: boolean;
  destructive?: boolean;
  description?: string;
  isSelected?: boolean;
  className?: string;
  /**
   * Disables element
   */
  disabled?: boolean;
  label: string;
  'data-testid'?: string;
};

export const ListItem = React.forwardRef(
  (props: ListItemProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const [isTooltipOpen, setTooltipOpen] = React.useState(false);
    const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (props.disabled) {
        if (props.disabledTooltip) {
          setTooltipOpen((prev) => !prev);
        }
        return;
      }
      return new Promise((resolve) => {
        if (props.onClick) {
          props.onClick(e).then(resolve);
        } else {
          resolve(null);
        }
      });
    };

    const content = (
      <div
        className={classNames('Mfw-ListItem-root', {
          'Mfw-ListItem-root--destructive': props.destructive,
          'Mfw-ListItem-root--disabled': props.disabled,
          'Mfw-ListItem-root--selected': props.isSelected,
          'Mfw-ListItem-root--withoutHover': props.withoutHover,
          'Mfw-ListItem-root--withoutSelectedHighlight':
            props.withoutSelectedHighlight,
        })}
        data-testid={props['data-testid']}
        onClick={handleClick}
        ref={ref}
      >
        <Typography sx={{ display: 'inline-flex' }}>
          {props.label}
          {props.tooltipLabelDescription && (
            <Tooltip title={props.tooltipLabelDescription}>
              <Iconify
                icon="mdi:information-outline"
                sx={{ marginLeft: '5px' }}
                size="small"
              />
            </Tooltip>
          )}
        </Typography>
        {props?.description && (
          <Typography className="Mfw-ListItem-description" variant="body2">
            {props.description}
          </Typography>
        )}
      </div>
    );

    if (!props.disabled) return content;
    return (
      <Tooltip
        onClose={() => setTooltipOpen(false)}
        onClick={() => setTooltipOpen(true)}
        title={props.disabledTooltip}
        leaveTouchDelay={5000}
        open={isTooltipOpen}
      >
        {content}
      </Tooltip>
    );
  },
);

ListItem.displayName = 'ListItem';
